import { useMemo, useEffect } from 'react'
import Alert from "@mui/material/Alert"
import Box from "@mui/material/Box"
import Checkbox from "@mui/material/Checkbox"
import FormControl from "@mui/material/FormControl"
import FormControlLabel from "@mui/material/FormControlLabel"
import InputLabel from "@mui/material/InputLabel"
import MenuItem from "@mui/material/MenuItem"
import Select, { type SelectChangeEvent } from "@mui/material/Select"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { ru } from 'date-fns/locale'
import { useTranslation } from "react-i18next"
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { 
  formatAbonementTimeValue,
  toPickerValue,
} from "./helpers"
import { useGetAbonementsQuery } from '@/api/abonements'
import { selectActiveBuildingId } from '@/features/building/buildingSlice'
import { useAppSelector } from '@/hooks/store'
import type { AbonementLinkForm } from '../types'
import AbonementDynamicHintAlert from './AbonementDynamicHintAlert'

type AbonementInputsProps = {
  abonementId: number | null
  abonementStartDate: string | Date | null
  abonementEndDate: string | Date | null
  setAbonementForm: (
    value: AbonementLinkForm | ((prev: AbonementLinkForm) => AbonementLinkForm)
  ) => void
}

const AbonementInputs = ({
  abonementId,
  abonementStartDate,
  abonementEndDate,
  setAbonementForm,
}: AbonementInputsProps) => {
  const { t } = useTranslation();
  const activeBuildingId = useAppSelector(selectActiveBuildingId)
  const {
    data: abonementsResponse,
    isLoading: abonementsLoading,
  } = useGetAbonementsQuery({ 
    building_id: Number(activeBuildingId)
  }, { 
    skip: !activeBuildingId,
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
  })

  const selectedPaymentCode = useMemo(() => {
    if (!abonementId) return ''
    const found = abonementsResponse?.list.find(
      (ab) => Number(ab.id) === Number(abonementId)
    )
    return found?.payment_code || ''
  }, [abonementsResponse?.list, abonementId])

  const selectedAbonement = useMemo(() => 
    abonementsResponse?.list.find(
      (item) => Number(item?.id) === Number(abonementId || 0)
    ) || null,
  [abonementsResponse?.list, abonementId])

  const selectedAbonementStartTime = formatAbonementTimeValue(
    selectedAbonement?.start_time
  )
  const selectedAbonementEndTime = formatAbonementTimeValue(
    selectedAbonement?.end_time
  )
  const isSelectedAbonementRoundTheClock =
    Boolean(selectedAbonement) &&
    !selectedAbonementStartTime &&
    !selectedAbonementEndTime
    
  const handleAbonementDateChange =
    (field: 'abonement_start' | 'abonement_end') => (newValue: unknown) => {
      setAbonementForm((prev) => ({
        ...prev,
        [field]: newValue instanceof Date ? newValue : null
      }))
    }

  const handleAbonementIdChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value
    const nextAbonement =
      abonementsResponse?.list.find((item) => item.payment_code === value) ||
      null
    setAbonementForm({
      abonement_id: nextAbonement?.id ? Number(nextAbonement.id) : null,
      abonement_start: nextAbonement?.start_at || null,
      abonement_end: nextAbonement?.expire_at || null
    })
  }

  useEffect(() => {
    if (!selectedAbonement) return
    setAbonementForm((prev) => {
      const nextStart = prev.abonement_start || selectedAbonement.start_at || null
      const nextEnd = prev.abonement_end || selectedAbonement.expire_at || null
      if (nextStart === prev.abonement_start && nextEnd === prev.abonement_end) return prev
      return { 
        ...prev, 
        abonement_start: nextStart, 
        abonement_end: nextEnd 
      }
    })
  }, [selectedAbonement, setAbonementForm])

  return (
    <>
      <AbonementDynamicHintAlert 
        abonementsLoading={abonementsLoading}
        selectedAbonement={selectedAbonement}
        allAbonements={abonementsResponse?.list}
        selectedAbonementStartTime={selectedAbonementStartTime}
        selectedAbonementEndTime={selectedAbonementEndTime}
        isSelectedAbonementRoundTheClock={isSelectedAbonementRoundTheClock}
        startDate={abonementStartDate}
        endDate={abonementEndDate}
      />
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>{t('choose_abonement')}</InputLabel>
        <Select
          name="abonement_id"
          value={selectedPaymentCode}
          onChange={handleAbonementIdChange}
          label={t('choose_abonement')}
          disabled={abonementsLoading}
        >
          {
            abonementsResponse?.uniqueList.length === 0 ? (
              <MenuItem value="" disabled>
                {t('no_abonements')}
              </MenuItem>
            ) : (
              abonementsResponse?.uniqueList.map((ab) => (
                <MenuItem
                  key={ab.payment_code || ab.id}
                  value={ab.payment_code || ''}
                >
                  {
                    `#${ab.name || '#'+ab.id} ${ab.payment_code || t('code')}`
                    + ' - ' + 
                    `${Number(ab.amount || 0).toLocaleString('ru-RU')} ₸`
                  } 
                </MenuItem>
              ))
            )
          }
        </Select>
      </FormControl>
      {
        selectedAbonement && (
        <>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {t('access_time_daily')}
          </Typography>
          {isSelectedAbonementRoundTheClock ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              24/7
            </Alert>
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                flexWrap: 'wrap',
                mb: 2
              }}
            >
              <TextField
                label={t('begin')}
                value={selectedAbonementStartTime || t('not_selected')}
                helperText={t('from_what_time')}
                fullWidth
                disabled
              />
              <TextField
                label={t('end')}
                value={selectedAbonementEndTime || t('not_selected')}
                helperText={t('to_what_time')}
                fullWidth
                disabled
              />
            </Box>
          )}
          <FormControlLabel
            sx={{ mb: 2 }}
            control={
              <Checkbox
                checked={Boolean(selectedAbonement.include_weekends)}
                disabled
              />
            }
            label={t('including_weekends')}
          />
        </>
      )}

      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ru}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
            mb: 3
          }}
        >
          <DatePicker
            label={`${t('abonement')}: ${t('start_date')}`}
            value={toPickerValue(abonementStartDate)}
            onChange={handleAbonementDateChange('abonement_start')}
            slotProps={{ textField: { fullWidth: true } }}
          />
          <DatePicker
            label={`${t('abonement')}: ${t('end_date')}`}
            value={toPickerValue(abonementEndDate)}
            onChange={handleAbonementDateChange('abonement_end')}
            slotProps={{ textField: { fullWidth: true } }}
          />
        </Box>
      </LocalizationProvider>
      {
        !abonementsLoading &&
        abonementsResponse?.list.length === 0 ? (
          <Alert severity="warning" sx={{ mb: 3 }}>
            {`${t('no_abonements')}. ${t('abonements')} -> ${t('addAbonement')}.`}
          </Alert>
        ) : null
      }
    </>
  )
}

export default AbonementInputs
